import { FormControl, Paper, styled, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Box } from '@mui/material';
import { blue } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Alert } from '@mui/material';
import { createDefaultDestination, getBoxList, GoodsInfo, MemberType, OrderInfo } from 'adoms-common-lib';
import React, { useEffect, useState } from 'react';
import { NumericFormat, PatternFormat } from 'react-number-format';
import { useLocation, useNavigate } from 'react-router-dom';
import { imageResizeAndConvertBase64 } from '../../common/ImageEditUtil';
import ApplicationTooBar from '../../components/molecule/ApplicationToolBar';
import OrderStepBar, { Process } from '../../components/molecule/OrderStepBar';
import { CustomerURLInformationUtil } from '../../common/customerURLInformationUtil';
import { DeliveryItemInput } from '../../components/organisms/DeliveryItemInput';
import { BusinessDestinationInfo } from 'adoms-common-lib/build/model/OrderInfo/BusinessDestinationInfo';
import { BusinessRequesterInfo, createDefaultRequester } from 'adoms-common-lib/build/model/OrderInfo/BusinessRequesterInfo';
import { ImageStoreButton } from '../../components/atoms/ImageStoreButton';

const useStyles = makeStyles()((theme) => ({
    root: {
        '& > *': {
            margin: theme.spacing(1),
        },
    },
    formControl: {
        margin: theme.spacing(1),
        alignItems: 'left',
        minWidth: 32,
    },
    paper: {
        padding: '12px',
        textAlign: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
    },
    title: {
        flexGrow: 1,
        textAlign: 'center',
        backgroundColor: blue[500],
        color: 'white',
    },
    name: {
        width: 180,
    },
    griditem: {
    },
    table: {
        minWidth: 120,
    },
    tel: {
        width: 180,
    },
    zipcode: {
        width: 140,
    },
    goodsweight: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 100,
    },
    alert: {
        marginBottom: theme.spacing(1),
        textAlign: "left"
    },
    paperAlert: {
        padding: theme.spacing(1),
    },
    paperSuccess: {
        padding: theme.spacing(1),
        marginTop: theme.spacing(0.5)
    },
    qrResult: {
        textAlign: "left"
    },
    noWrap: {
        whiteSpace: 'nowrap'
    }
}));

const OrderTextField = styled(TextField)(({ theme }) => ({
    width: 180,
}));


interface NumberFormatCustomProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
}

/**
 * 電話番号のフォーマット
 * @param props 
 */
const TelNumberFormat = React.forwardRef(function NumberFormatCustom(
    props: NumberFormatCustomProps,
    ref,
) {
    const { onChange, ...other } = props;

    return (
        <PatternFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            valueIsNumericString
            format="###########"
        />
    );
});

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(
    props: NumberFormatCustomProps,
    ref,
) {
    const { onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            defaultValue={0}
            inputMode='decimal'
            thousandSeparator
            valueIsNumericString
            allowNegative={false}
            suffix="g"
        />
    );
});

//　TODO: 共通化
const genId = () => {
    // Prefer crypto.randomUUID when available
    // Fallback to a simple unique string
    // @ts-ignore
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `id_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

export default function BusinessOrderGoodAndDestinationInput() {
    const navigate = useNavigate();
    const { classes } = useStyles();
    const location = useLocation();
    const initialMaxWeight = 6712; // TEMPORARY: 2025年医療実証最大重量からペイロードボックスの重さ（1288g）を差し引いた値
    const customerURLInformation = CustomerURLInformationUtil.instance.getCustomerURLInformation();
    const order = location.state?.order as OrderInfo;
    const orderForEdit = location.state?.orderForEdit as OrderInfo;
    const destinationLocalVal = orderForEdit?.destination as BusinessDestinationInfo ?? createDefaultDestination();
    const requesterLocalVal = orderForEdit?.requester as BusinessRequesterInfo ?? createDefaultRequester();
    const qrCodeErrorList = location.state?.qrCodeErrorList as string[];
    const displayQrCodeScanResult = location.state?.displayQrCodeScanResult as string;

    const [maxWeight, setMaxWeight] = useState(initialMaxWeight);  // TEMPORARY: 2025年医療実証最大重量からペイロードボックスの重さ（仮で1kg）を差し引いた値
    const [destinationCompanyName, setDestinationCompanyName] = useState(destinationLocalVal.companyName ? destinationLocalVal.companyName : "");
    const [destinationFirstname, setDestinationFirstName] = useState(destinationLocalVal.firstname ? destinationLocalVal.firstname : "");
    const [destinationLastName, setDestinationLastName] = useState(destinationLocalVal.lastname ? destinationLocalVal.lastname : "");
    const [destinationTel, setDestinationTel] = React.useState(destinationLocalVal.tel ? destinationLocalVal.tel : "");
    const [destinationTel2, setDestinationPhone2] = React.useState(destinationLocalVal.tel2 ? destinationLocalVal.tel2 : "");
    const [destinationEmail, setDestinationEmail] = React.useState(destinationLocalVal.mailaddress ? destinationLocalVal.mailaddress : "");
    const [destinationPrefecture, setDestinationPrefecture] = React.useState(destinationLocalVal.prefecture ? destinationLocalVal.prefecture : order.arrival.inputDefaultPrefecture);
    const [destinationAddress, setDestinationAddress] = React.useState(destinationLocalVal.address ? destinationLocalVal.address : order.arrival.inputDefaultCity + order.arrival.inputDefaultAddress);
    const [imageFile, setImageFile] = React.useState<File | undefined>();

    const [requesterCompanyName, setRequesterCompanyName] = useState(requesterLocalVal.companyName ? requesterLocalVal.companyName : "");
    const [requesterFirstname, setRequesterFirstName] = React.useState(requesterLocalVal.firstname ? requesterLocalVal.firstname : "");
    const [requesterLastname, setRequesterLastName] = React.useState(requesterLocalVal.lastname ? requesterLocalVal.lastname : "");
    const [requesterEmail, setRequesterEmail] = React.useState(requesterLocalVal.mailaddress ? requesterLocalVal.mailaddress : "");
    const [requesterTel, setRequesterTel] = React.useState(requesterLocalVal.tel ? requesterLocalVal.tel : "");
    const [inputWeight, setInputWeight] = React.useState<number | undefined>(orderForEdit?.goods[0]?.weight ? orderForEdit.goods[0]?.weight : undefined);
    const [deductWeight, setDeductWeight] = React.useState<number | undefined>(location.state?.deductWeight ? location.state.deductWeight : undefined);
    // 初期配送物リスト（既存編集データ > 受け取った注文 > デフォルト）
    const [goodsItems, setGoodsItems] = useState<GoodsInfo[]>(
        orderForEdit?.goods?.length
            ? orderForEdit.goods
            : order?.goods?.length
                ? order.goods
                : [{ id: genId(), weight: inputWeight ?? 0, name: '', count: 1, packageType: "指定なし", handlingInstructions: ["指定なし"] }]
    );
    const [totalWeightString, setTotalWeightString] = useState("0");
    const [remainWeightString, setRemainWeightString] = useState(new Intl.NumberFormat().format(maxWeight));
    const [remainWeightNum, setRemainWeightNum] = useState(0);

    useEffect(() => {
        if (customerURLInformation === null) {
            navigate("/forbidden403Page");
        };

        const initialDeductWeight = location.state.deductWeight || inputWeight || 0;
        const initialWeight = inputWeight || 0;;
        const nf: Intl.NumberFormat = new Intl.NumberFormat();
        setTotalWeightString(nf.format(initialWeight));

        const newRemainWeight = maxWeight - initialDeductWeight - initialWeight;
        setRemainWeightNum(newRemainWeight);
        setRemainWeightString(nf.format(newRemainWeight));
    }, []);


    const handleOnChangeRequesterCompanyName = (event: any) => {
        setRequesterCompanyName(event.target.value);
    }

    const handleOnChangeFirstName = (event: any) => {
        setRequesterFirstName(event.target.value);
    }

    const handleOnChangeLastName = (event: any) => {
        setRequesterLastName(event.target.value);
    }

    const handleOnChangeTel = (event: any) => {
        setRequesterTel(event.target.value);
    }

    const handleOnChangeGoodsItems = (items: GoodsInfo[]) => {
        console.log("handleOnChangeGoodsItems")
        const boxList = getBoxList();

        let deductWeight = 0;
        items.forEach(item => {
            const box = boxList.find(box => box.boxName === item.packageType);
            if (box) {
                deductWeight += box.boxWeight;
                if (item.handlingInstructions?.includes("保冷（2-5℃（医薬品））") || item.handlingInstructions?.includes("2-6℃（血液）")) {
                    deductWeight += box.refrigeratedWeight ?? 0;
                } else if (item.handlingInstructions?.includes("-20℃以下（血液）")) {
                    deductWeight += box.frozenWeight ?? 0;
                }
            }
        });

        setDeductWeight(deductWeight);
        const nf: Intl.NumberFormat = new Intl.NumberFormat();
        setMaxWeight(initialMaxWeight - deductWeight);
        const newRemainWeight = initialMaxWeight - deductWeight - (inputWeight ?? 0);
        setRemainWeightNum(newRemainWeight);
        setRemainWeightString(nf.format(newRemainWeight));

        setGoodsItems(items);
    }

    /**
     * 重量が変更されたときのハンドラ
     * @param event 
     */
    const handleChangeWeight = (event: React.ChangeEvent<HTMLInputElement>) => {
        let weight = Number(event.target.value);
        setInputWeight(weight);
        // 合計重量計算（複数品目の場合は最初の品目に重量を反映させる運用）
        setGoodsItems(prev => {
            if (!prev.length) return prev;
            const next = [...prev];
            next[0] = { ...next[0], weight };
            return next;
        });
        //数字3桁カンマ区切りのフォーマット
        const nf: Intl.NumberFormat = new Intl.NumberFormat();

        // 合計重量
        setTotalWeightString(nf.format(weight));

        //残重量
        setRemainWeightNum(maxWeight - weight);
        setRemainWeightString(nf.format(maxWeight - weight));
    };

    function isNextDisable(): boolean {
        if (
            requesterFirstname !== ""
            && requesterLastname !== ""
            && requesterCompanyName !== ""
            && requesterEmail !== ""
            && requesterTel !== ""
            && destinationCompanyName !== ""
            && destinationFirstname !== ""
            && destinationLastName !== ""
            && destinationEmail !== ""
            && remainWeightNum >= 0
        ) {
            return false;
        } else {
            return true;
        }
    }

    /**
     * 次のページに遷移する際の処理
     * @param event 
     * @param navigate 
     */
    const handleToNextPage = async (event: any, order: OrderInfo) => {
        // 複数配送物を反映
        let goodsToSave = goodsItems.length
            ? goodsItems.map(g => g.name == "" ? ({ ...g, name: "荷物" }) : g)
            : [{ id: genId(), weight: inputWeight ?? 0, name: '荷物一式', count: 1 }];
        if (typeof inputWeight !== 'undefined' && goodsToSave.length) {
            goodsToSave[0].weight = inputWeight;
        }
        order.goods = goodsToSave;
        // let orderForEditDestination = order.destination as BusinessDestinationInfo ?? createDefaultDestination();
        // let orderForEditRequester = order.requester as BusinessRequesterInfo ?? createDefaultRequester();
        requesterLocalVal.type = MemberType.Business;
        requesterLocalVal.companyName = requesterCompanyName
        requesterLocalVal.id = "empty";
        requesterLocalVal.firstname = requesterFirstname;
        requesterLocalVal.lastname = requesterLastname;
        requesterLocalVal.mailaddress = requesterEmail;
        requesterLocalVal.zipcode = "***";
        requesterLocalVal.tel = requesterTel;
        destinationLocalVal.type = MemberType.Business;
        destinationLocalVal.companyName = destinationCompanyName
        destinationLocalVal.id = "empty";
        destinationLocalVal.firstname = destinationFirstname;
        destinationLocalVal.lastname = destinationLastName;
        destinationLocalVal.mailaddress = destinationEmail;
        destinationLocalVal.address = destinationAddress;
        destinationLocalVal.zipcode = "***";
        destinationLocalVal.prefecture = destinationPrefecture;
        destinationLocalVal.tel = destinationTel;
        destinationLocalVal.tel2 = destinationTel2;
        order.destination = destinationLocalVal;
        order.requester = requesterLocalVal;

        /*
        navigateのstateとしてFileオブジェクトを格納できないため、
        予めBase64形式に変換しておく。
        */
        let imageDataForSend = await new Promise<
            { imageBase64: string, imageUrl: string } | undefined
        >((resolve, reject) => {
            if (typeof imageFile !== 'undefined') {
                return resolve(imageResizeAndConvertBase64(imageFile));
            } else {
                return resolve(undefined);
            }
        }).catch(err => {
            let error = err as Error;
            console.log(error);
            throw error;
        });

        // 箱の重さは確認画面でプラスするため別途送信
        navigate(
            "/orderconfirm/" + customerURLInformation?.urlId,
            {
                state: {
                    order: order,
                    deductWeight: deductWeight,
                    imageBase64: imageDataForSend?.imageBase64 ?? undefined,
                    imageUrl: imageDataForSend?.imageUrl ?? undefined,
                    displayQrCodeScanResult: displayQrCodeScanResult,
                    qrCodeErrorList: qrCodeErrorList
                },
            })
    }

    const colItemStyle = { width: '25%', paddingLeft: 10, marginLeft: 0 };
    const colValueStyle = { width: '75%', padding: '0', verticalAlign: 'middle' };

    /**
     * 前のページに遷移する際の処理
     * @param event 
     * @param navigate 
     */
    const handleToBackPage = async (event: any, order: OrderInfo) => {
        let goodsToSave = goodsItems.length
            ? goodsItems.map(g => ({ ...g }))
            : [{ id: genId(), weight: inputWeight ?? 0, name: '荷物一式', count: 1 }];
        if (typeof inputWeight !== 'undefined' && goodsToSave.length) {
            goodsToSave[0].weight = inputWeight;
        }
        order.goods = goodsToSave;
        // let orderForEditDestination = order.destination as BusinessDestinationInfo ?? createDefaultDestination();
        // let orderForEditRequester = order.requester as BusinessRequesterInfo ?? createDefaultRequester();
        requesterLocalVal.type = MemberType.Business;
        requesterLocalVal.companyName = requesterCompanyName
        requesterLocalVal.id = "empty";
        requesterLocalVal.firstname = requesterFirstname;
        requesterLocalVal.lastname = requesterLastname;
        requesterLocalVal.mailaddress = requesterEmail;
        requesterLocalVal.zipcode = "***";;
        requesterLocalVal.tel = requesterTel;
        destinationLocalVal.type = MemberType.Business;
        destinationLocalVal.companyName = destinationCompanyName
        destinationLocalVal.id = "empty";
        destinationLocalVal.firstname = destinationFirstname;
        destinationLocalVal.lastname = destinationLastName;
        destinationLocalVal.mailaddress = destinationEmail;
        destinationLocalVal.address = destinationAddress;
        destinationLocalVal.zipcode = "***";
        destinationLocalVal.prefecture = destinationPrefecture;
        destinationLocalVal.tel = destinationTel;
        destinationLocalVal.tel2 = destinationTel2;
        order.destination = destinationLocalVal;
        order.requester = requesterLocalVal;

        navigate(
            "/orderflightsearchinput",
            {
                state: {
                    orderForEdit: order,
                    displayQrCodeScanResult: displayQrCodeScanResult,
                    qrCodeErrorList: qrCodeErrorList
                },
            });
    };

    return (
        <div>
            <Grid container justifyContent="center" xs={12} spacing={1}>
                <Grid item xs={12} sm={12} >
                    <ApplicationTooBar label="配送依頼" />
                </Grid>

                <Grid item xs={12} sm={12} className={classes.griditem}>
                    <OrderStepBar process={Process.DestinationInput} />
                    {(qrCodeErrorList && qrCodeErrorList.length !== 0) || displayQrCodeScanResult ?
                        <Paper className={classes.paperAlert} elevation={0}>
                            {qrCodeErrorList?.map((qrCodeError, index) => (
                                <Alert
                                    severity="error"
                                    className={classes.alert}>
                                    {qrCodeError}
                                </Alert>
                            ))}
                            <Alert severity="success"
                                className={classes.qrResult}>{"QRコード読み取り結果"}<br />{displayQrCodeScanResult}</Alert>
                        </Paper>
                        : undefined
                    }
                </Grid>
                <Grid item xs={12} sm={12} className={classes.griditem}>
                    <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                        配送物の情報
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12} className={classes.griditem}>
                    <DeliveryItemInput
                        title="配送物の情報"
                        items={goodsItems}
                        onChange={handleOnChangeGoodsItems}
                    />
                </Grid>
                <Grid item xs={12} sm={12} className={classes.griditem}>
                    <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                        配送物重量
                    </Typography>
                </Grid>
                <Grid key={0} item xs={12} sm={12} className={classes.griditem}>
                    <Paper className={classes.paper} elevation={0}>
                        <FormControl variant="standard" className={classes.formControl}>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        className={classes.goodsweight}
                                        label="重量"
                                        onChange={(e: any) => handleChangeWeight(e)}
                                        name="weight"
                                        id="formatted-numberformat-input"
                                        value={inputWeight}
                                        variant="outlined"
                                        InputProps={{
                                            inputComponent: NumberFormatCustom as any,
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8} className={classes.noWrap}>
                                    <Box textAlign={'left'}>
                                        <Typography variant="caption" color="textSecondary" textAlign='left'>
                                            ・輸送容器を指定した場合は
                                            <br />　積載する現物のみの重量を入力してください
                                            <br />・輸送容器を指定していない場合は
                                            <br />　容器の重量も含めた総重量を入力してください
                                            <br />・配送物が2個の場合は
                                            <br />　すべての合計重量を入力してください
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </FormControl>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Paper className={classes.paper} elevation={0}>
                        <Typography color={remainWeightNum >= 0 ? "inherit" : "error"}>合計重量：{totalWeightString} g</Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Paper className={classes.paper} elevation={0}>
                        <Typography color={remainWeightNum >= 0 ? "inherit" : "error"}>配送可能な残重量：{remainWeightString} g</Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={12} className={classes.griditem}>
                    <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                        荷送人様の情報
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12} className={classes.griditem}>
                    <TableContainer component={Paper} elevation={0}>
                        <Table className={classes.table} size="small" aria-label="simple table">
                            <TableBody>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>法人名</TableCell>
                                    <TableCell align="left" style={colValueStyle}>
                                        <FormControl variant="standard" className={classes.formControl} style={{ padding: 1 }}>
                                            <Grid container justifyContent="center" xs={12} style={{ marginRight: 15 }} spacing={0}>
                                                <Grid item xs={12} sm={12} className={classes.griditem}>
                                                    <TextField
                                                        error={!requesterCompanyName}
                                                        className={classes.name}
                                                        label="法人名"
                                                        onChange={(e) => handleOnChangeRequesterCompanyName(e)}
                                                        name="RequesterCompanyName"
                                                        id="Requester-companyname"
                                                        variant="outlined"
                                                        margin="dense"
                                                        value={requesterCompanyName} />
                                                </Grid>
                                            </Grid>
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>お名前</TableCell>
                                    <TableCell align="left" style={colValueStyle}>
                                        <FormControl variant="standard" className={classes.formControl} style={{ padding: 1 }}>
                                            <Grid container justifyContent="center" xs={12} style={{ marginRight: 15 }} spacing={0}>
                                                <Grid item xs={12} sm={6} className={classes.griditem}>
                                                    <TextField
                                                        error={!requesterLastname}
                                                        className={classes.name}
                                                        label="姓"
                                                        onChange={(e) => handleOnChangeLastName(e)}
                                                        name="requesterLastName"
                                                        id="requester-lastname"
                                                        variant="outlined"
                                                        margin="dense"
                                                        value={requesterLastname} />
                                                </Grid>
                                                <Grid item xs={12} sm={6} className={classes.griditem}>
                                                    <TextField
                                                        error={!requesterFirstname}
                                                        className={classes.name}
                                                        label="名"
                                                        onChange={(e) => handleOnChangeFirstName(e)}
                                                        name="firstName"
                                                        id="requester-first"
                                                        variant="outlined"
                                                        margin="dense"
                                                        value={requesterFirstname} />
                                                </Grid>
                                            </Grid>
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>メールアドレス</TableCell>
                                    <TableCell align="left" style={colValueStyle}>
                                        <FormControl className={classes.formControl}>
                                            <TextField
                                                error={!requesterEmail}
                                                label="例）example@example.com"
                                                margin="dense"
                                                onChange={(e) => setRequesterEmail(e.target.value)}
                                                name="email"
                                                id="requester-email"
                                                variant="outlined"
                                                value={requesterEmail}
                                                type="email"
                                            />
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>
                                        電話番号
                                    </TableCell>
                                    <TableCell align="left" component="td" style={colValueStyle} valign="middle">
                                        <FormControl
                                            variant="standard"
                                            className={classes.formControl}
                                            style={{ marginRight: 0 }}>
                                            <TextField
                                                className={classes.tel}
                                                error={!requesterTel}
                                                onChange={(e) => handleOnChangeTel(e)}
                                                name="tel"
                                                label="例）09011112222"
                                                margin="dense"
                                                id="requester-tel-first"
                                                variant="outlined"
                                                helperText={<span>ハイフン（-）不要</span>}
                                                value={requesterTel}
                                                InputProps={{
                                                    inputComponent: TelNumberFormat as any,
                                                }} />
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>注文内容<br /></TableCell>
                                    <TableCell style={{ padding: 8 }}>
                                        <div style={{ width: '75%', wordBreak: 'break-all' }}>
                                            {imageFile !== undefined ? imageFile.name : ""}
                                        </div>
                                        <ImageStoreButton
                                            inputFile={imageFile}
                                            setImageFile={setImageFile}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>

                <Grid item xs={12} sm={12} className={classes.griditem}>
                    <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                        荷受人様の情報
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={12} className={classes.griditem}>
                    <TableContainer component={Paper} elevation={0}>
                        <Table className={classes.table} size="small" aria-label="simple table">
                            <TableBody>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>法人名</TableCell>
                                    <TableCell align="left" style={colValueStyle}>
                                        <FormControl variant="standard" className={classes.formControl} style={{ padding: 1 }}>
                                            <Grid container justifyContent="center" xs={12} style={{ marginRight: 15 }} spacing={0}>
                                                <Grid item xs={12} sm={12} className={classes.griditem}>
                                                    <TextField
                                                        error={!destinationCompanyName}
                                                        className={classes.name}
                                                        label="法人名"
                                                        onChange={(e) => setDestinationCompanyName(e.target.value)}
                                                        name="DestinationCompanyName"
                                                        id="-companyname"
                                                        variant="outlined"
                                                        margin="dense"
                                                        value={destinationCompanyName} />
                                                </Grid>
                                            </Grid>
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>お名前</TableCell>
                                    <TableCell align="left" style={colValueStyle}>
                                        <FormControl variant="standard" className={classes.formControl} style={{ padding: 1 }}>
                                            <Grid container justifyContent="center" xs={12} style={{ marginRight: 15 }} spacing={0}>
                                                <Grid item xs={12} sm={6} className={classes.griditem}>
                                                    <TextField
                                                        error={!destinationLastName}
                                                        className={classes.name}
                                                        label="姓"
                                                        onChange={(e) => setDestinationLastName(e.target.value)}
                                                        name="destinationLastName"
                                                        id="destination-lastname"
                                                        variant="outlined"
                                                        margin="dense"
                                                        value={destinationLastName} />
                                                </Grid>
                                                <Grid item xs={12} sm={6} className={classes.griditem}>
                                                    <TextField
                                                        error={!destinationFirstname}
                                                        className={classes.name}
                                                        label="名"
                                                        onChange={(e) => setDestinationFirstName(e.target.value)}
                                                        name="destinationFirstName"
                                                        id="destination-firstname"
                                                        variant="outlined"
                                                        margin="dense"
                                                        value={destinationFirstname} />
                                                </Grid>
                                            </Grid>
                                        </FormControl>

                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>メールアドレス</TableCell>
                                    <TableCell align="left" style={colValueStyle}>
                                        <FormControl className={classes.formControl}>
                                            <TextField
                                                label="例）example@example.com"
                                                error={!destinationEmail}
                                                margin="dense"
                                                onChange={(e) => setDestinationEmail(e.target.value)}
                                                name="destinationEmail"
                                                id="destination-email"
                                                variant="outlined"
                                                value={destinationEmail}
                                                type="email"
                                            />
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="right" component="th" scope="" style={colItemStyle}>
                                        電話番号
                                    </TableCell>
                                    <TableCell align="left" component="td" style={colValueStyle} valign="middle">
                                        <FormControl
                                            variant="standard"
                                            className={classes.formControl}
                                            style={{ marginRight: 0 }}>
                                            <TextField
                                                className={classes.tel}
                                                onChange={(e) => setDestinationTel(e.target.value)}
                                                name="tel2"
                                                label="例）09011112222"
                                                margin="dense"
                                                id="requester-tel-first"
                                                variant="outlined"
                                                helperText={<span>ハイフン（-）不要</span>}
                                                value={destinationTel}
                                                InputProps={{
                                                    inputComponent: TelNumberFormat as any,
                                                }} />
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>

                <Grid item xs={6} sm={6} className={classes.griditem}>
                    <Paper className={classes.paper} elevation={0} >
                        <FormControl variant="standard" className={classes.formControl}>
                            {orderForEdit ?
                                <IconButton
                                    edge="start"
                                    color="primary"
                                    aria-label="menu"
                                    onClick={(e) => { handleToBackPage(e, orderForEdit) }}
                                    size="large">
                                    <ArrowBackIosIcon /><Typography>戻る</Typography>
                                </IconButton>
                                : undefined
                            }
                            {order ?
                                <IconButton
                                    edge="start"
                                    color="primary"
                                    aria-label="menu"
                                    onClick={(e) => { handleToBackPage(e, order) }}
                                    size="large">
                                    <ArrowBackIosIcon /><Typography>戻る</Typography>
                                </IconButton>
                                : undefined
                            }
                        </FormControl>
                    </Paper>
                </Grid>

                <Grid item xs={6} sm={6} className={classes.griditem}>
                    <Paper className={classes.paper} elevation={0}>
                        <FormControl variant="standard" className={classes.formControl}>
                            {orderForEdit ?
                                <IconButton
                                    edge="start"
                                    color="primary"
                                    aria-label="menu"
                                    onClick={(e) => { handleToNextPage(e, orderForEdit) }}
                                    disabled={isNextDisable()}
                                    size="large">
                                    <Typography>次へ</Typography><ArrowForwardIosIcon />
                                </IconButton>
                                : undefined
                            }
                            {order ?
                                <IconButton
                                    edge="start"
                                    color="primary"
                                    aria-label="menu"
                                    onClick={(e) => { handleToNextPage(e, order) }}
                                    disabled={isNextDisable()}
                                    size="large">
                                    <Typography>次へ</Typography><ArrowForwardIosIcon />
                                </IconButton>
                                : undefined
                            }
                        </FormControl>
                    </Paper>
                </Grid>
            </Grid>
        </div>
    );
}